/**
 * AddSourceDialog
 *
 * Dialog to add insight items from ANY actor in the project to a cluster.
 * Loads audiences + actors from Firestore (not just from analysis actorRefs).
 * This allows adding sources from actors that weren't in the original analysis.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Slider,
  CircularProgress,
  ListSubheader,
} from '@mui/material';
import { SDialog } from '@modules/ui-components';
import { ActorService } from '@modules/firebase-core/services/ActorService';
import { AudienceService } from '@modules/firebase-core/services/AudienceService';
import type { Actor, Audience } from '@segmently/schema-models/entities';

// ── Types ────────────────────────────────────────────────────────────

interface InsightSource {
  actorId: string;
  actorName: string;
  audienceId: string;
  originalItemId: string;
  originalDescription: string;
  originalWeight: number;
  similarityScore: number;
}

interface AddSourceDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (sources: InsightSource[]) => void;
  category: string;
  currentSources: InsightSource[];
  actorRefs: any[]; // kept for reference but not used as actor source
  projectId: string;
  clusterDescription: string;
}

// ── Actor option for dropdown ────────────────────────────────────────

interface ActorOption {
  actorId: string;
  actorName: string;
  audienceId: string;
  audienceName: string;
}

// ── Component ────────────────────────────────────────────────────────

export const AddSourceDialog: React.FC<AddSourceDialogProps> = ({
  open,
  onClose,
  onAdd,
  category,
  currentSources,
  projectId,
  clusterDescription,
}) => {
  const [selectedActorKey, setSelectedActorKey] = useState<string>(''); // "audienceId:actorId"
  const [actorData, setActorData] = useState<Actor | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingActors, setLoadingActors] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [similarityScore, setSimilarityScore] = useState<number>(0.7);

  // All actors from Firestore (grouped by audience)
  const [allActors, setAllActors] = useState<ActorOption[]>([]);

  // Items already in this cluster
  const currentSourceItemIds = useMemo(
    () => new Set(currentSources.map(s => s.originalItemId)),
    [currentSources],
  );

  // ── Load all audiences → actors from Firestore on dialog open ──────

  useEffect(() => {
    if (!open || !projectId) return;

    let cancelled = false;
    setLoadingActors(true);

    const loadAllActors = async () => {
      try {
        const audienceService = new AudienceService(projectId);
        const audiences = await audienceService.list({ limit: 50 });

        const actors: ActorOption[] = [];
        for (const audience of audiences) {
          const actorService = new ActorService(projectId, audience.id!);
          const actorList = await actorService.list({ limit: 100 });
          for (const actor of actorList) {
            actors.push({
              actorId: actor.id!,
              actorName: actor.name || 'Unnamed',
              audienceId: audience.id!,
              audienceName: audience.name || 'Unnamed Audience',
            });
          }
        }

        if (!cancelled) {
          setAllActors(actors);
          setLoadingActors(false);
        }
      } catch (err) {
        console.error('Failed to load actors:', err);
        if (!cancelled) setLoadingActors(false);
      }
    };

    loadAllActors();
    return () => { cancelled = true; };
  }, [open, projectId]);

  // ── Load selected actor's JTBD when picked ────────────────────────

  useEffect(() => {
    if (!selectedActorKey || !open) {
      setActorData(null);
      return;
    }

    const [audienceId, actorId] = selectedActorKey.split(':');
    if (!audienceId || !actorId) return;

    setLoading(true);
    const service = new ActorService(projectId, audienceId);
    service.getWithSubcollections(actorId)
      .then(setActorData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedActorKey, open, projectId]);

  // Items from selected actor for this category (excluding already-added)
  const availableItems = useMemo(() => {
    if (!actorData) return [];
    const items = (actorData as any)[category] || [];
    return items.filter((item: any) => !currentSourceItemIds.has(item.id));
  }, [actorData, category, currentSourceItemIds]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setSelectedActorKey('');
      setActorData(null);
      setSelectedItems(new Set());
      setSimilarityScore(0.7);
    }
  }, [open]);

  const toggleItem = (id: string) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAdd = () => {
    if (selectedItems.size === 0 || !actorData || !selectedActorKey) return;

    const [audienceId, actorId] = selectedActorKey.split(':');
    const actorOption = allActors.find(a => a.actorId === actorId && a.audienceId === audienceId);
    if (!actorOption) return;

    const items = (actorData as any)[category] || [];
    const newSources: (InsightSource & { audienceName: string })[] = items
      .filter((item: any) => selectedItems.has(item.id))
      .map((item: any) => ({
        actorId: actorOption.actorId,
        actorName: actorOption.actorName,
        audienceId: actorOption.audienceId,
        audienceName: actorOption.audienceName,
        originalItemId: item.id,
        originalDescription: item.description || '',
        originalWeight: item.weight || 1,
        similarityScore,
      }));

    onAdd(newSources);
    onClose();
  };

  // Group actors by audience for display
  const actorsByAudience = useMemo(() => {
    const map = new Map<string, ActorOption[]>();
    for (const actor of allActors) {
      const key = actor.audienceName;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(actor);
    }
    return map;
  }, [allActors]);

  return (
    <SDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title="Add source to cluster"
      primaryAction={{
        label: `Add ${selectedItems.size} item${selectedItems.size !== 1 ? 's' : ''}`,
        onClick: handleAdd,
        disabled: selectedItems.size === 0,
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Cluster: &ldquo;{clusterDescription.slice(0, 100)}&rdquo;
      </Typography>

      {/* Actor selector — grouped by audience, loaded from Firestore */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Select actor</InputLabel>
        <Select
          value={selectedActorKey}
          onChange={(e) => { setSelectedActorKey(e.target.value); setSelectedItems(new Set()); }}
          label="Select actor"
          disabled={loadingActors}
        >
          {loadingActors && <MenuItem disabled>Loading actors...</MenuItem>}
          {[...actorsByAudience.entries()].map(([audienceName, actors]) => [
            <ListSubheader key={`header-${audienceName}`} sx={{ bgcolor: 'background.paper' }}>
              {audienceName}
            </ListSubheader>,
            ...actors.map(actor => (
              <MenuItem key={`${actor.audienceId}:${actor.actorId}`} value={`${actor.audienceId}:${actor.actorId}`}>
                {actor.actorName}
              </MenuItem>
            )),
          ])}
        </Select>
      </FormControl>

      {/* Items list */}
      {loading && (
        <Box display="flex" alignItems="center" gap={1} py={2}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading actor data...</Typography>
        </Box>
      )}

      {!loading && selectedActorKey && availableItems.length === 0 && actorData && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No available {category} items from this actor (all already in cluster or actor has none).
        </Typography>
      )}

      {!loading && availableItems.length > 0 && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Select {category} items to add:
          </Typography>
          <List dense sx={{ maxHeight: 250, overflow: 'auto', bgcolor: 'action.hover', borderRadius: 1, mb: 2 }}>
            {availableItems.map((item: any) => (
              <ListItem key={item.id} disablePadding>
                <ListItemButton onClick={() => toggleItem(item.id)} dense>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox checked={selectedItems.has(item.id)} edge="start" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.description}
                    secondary={<Chip label={`w:${item.weight}`} size="small" sx={{ mt: 0.5 }} />}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          {/* Similarity score */}
          <Typography variant="subtitle2" gutterBottom>
            Similarity score: {similarityScore.toFixed(1)}
          </Typography>
          <Slider
            value={similarityScore}
            onChange={(_, v) => setSimilarityScore(v as number)}
            min={0.5}
            max={1.0}
            step={0.1}
            marks
            valueLabelDisplay="auto"
            sx={{ mb: 1 }}
          />
        </>
      )}
    </SDialog>
  );
};
